// Coordinator self-serve signup (Phase 0, Track A).
//
// One route, one page, one visible step at a time. Steps are client
// state, not sub-routes — /:parish/* owns every two-segment path, so
// step URLs would collide with parish slugs. Wizard fields (never the
// password) persist to localStorage so a refresh — or the round trip
// through a confirmation email — resumes where the coordinator left
// off. Resume is server-derived where possible: on boot the wizard
// asks /api/onboarding/signup (idempotent) what already exists and
// routes to the right step from the parish's own state.
//
// Flow: Step 1 creates the Supabase auth user (role "coordinator" in
// metadata, which the handle_new_volunteer trigger ignores), then
// POST /api/onboarding/signup creates the parish + users row through
// the worker's service role. Step 2 submits the MSP API token to
// /api/onboarding/msp-validate, which validates, stores, and
// provisions (trialing status + template seed). Step 3 is optional
// branding through the existing admin settings/logo endpoints — the
// coordinator has a users row by now, so the admin role gate passes.
// "Skip" is deliberate on both 2 and 3: coordinators often need to
// ask their office admin for the token, and signup must not block on
// polish.

import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { supabase, isSupabaseEnabled } from "../lib/supabaseClient";
import {
  apiPatch,
  apiPostEnvelope,
  apiPostFormData,
  type ApiEnvelopeError,
} from "../lib/api";
import { useLocalStorage } from "../lib/useLocalStorage";
import AuthScreenHeader from "../components/AuthScreenHeader";
import { timezoneOptions } from "../lib/us-timezones";
import ConfirmCodeStep from "../components/ConfirmCodeStep";
import { classifySignupResult, RESEND_COOLDOWN_SECONDS } from "../lib/otp";
import PoweredByFooter from "../components/PoweredByFooter";

const MIN_PASSWORD_LENGTH = 8;
const WIZARD_KEY = "sacristy.signup.v1";
const HEX_RE = /^#[0-9A-Fa-f]{6}$/;
// Mirrors the server's guards in routes/parish.ts.
const ALLOWED_LOGO_MIMES = "image/png,image/svg+xml,image/jpeg";
const MAX_LOGO_BYTES = 500_000;

interface WizardState {
  parishName: string;
  coordinatorName: string;
  email: string;
  // IANA zone for parishes.timezone (migration 0012). Optional so
  // wizard state persisted before this field existed still loads.
  timezone?: string;
  connectSkipped?: boolean;
}

const EMPTY_WIZARD: WizardState = { parishName: "", coordinatorName: "", email: "" };

// Computed once per module load — the option list doesn't change
// mid-session and the detected zone is the right initial pick.
const TZ = timezoneOptions();

interface ParishSummary {
  id: string;
  name: string;
  slug: string;
  status: string;
  msp_connected: boolean;
}

interface MspValidateResult {
  parishName: string;
  volunteerCount: number;
  ministryCount: number;
  ministryNames: string[];
}

type Phase =
  | "boot"
  | "account"
  | "authed-account"
  // Enter the emailed 6-digit code (confirmation hotfix — codes
  // replace scanner-vulnerable links).
  | "confirm-code"
  | "creating-parish"
  | "create-failed"
  | "connect"
  | "brand"
  | "done"
  | "already-onboarded"
  | "blocked";

// Same reasoning as VolunteerSignup's describeSignupError: on the
// SIGNUP form, "this email already has an account" is helpful
// guidance, not an enumeration risk.
function describeSignupError(message: string | undefined): string {
  if (!message) return "Something went wrong creating your account. Please try again.";
  const m = message.toLowerCase();
  if (m.includes("already registered") || m.includes("already exists")) {
    return "You already have an account with this email. Try signing in instead.";
  }
  return message;
}

function describeMspError(error: ApiEnvelopeError | null): string {
  if (error?.message) return error.message;
  switch (error?.kind) {
    case "invalid_token":
      return "MSP rejected this API token. Double-check you copied the full token.";
    case "not_plus_edition":
      return "MSP refused API access — this usually means the account isn't Plus Edition.";
    case "msp_unreachable":
      return "Ministry Scheduler Pro didn't respond. Please try again in a minute.";
    default:
      return "Something went wrong checking the token. Please try again.";
  }
}

const STEPS = [
  { key: "account", label: "Account" },
  { key: "connect", label: "Connect MSP" },
  { key: "brand", label: "Branding" },
] as const;

type StepKey = (typeof STEPS)[number]["key"];

function StepIndicator({ current }: { current: StepKey }) {
  const currentIndex = STEPS.findIndex((s) => s.key === current);
  return (
    <ol className="signup__steps">
      {STEPS.map((s, i) => (
        <li
          key={s.key}
          className={
            i === currentIndex
              ? "signup__step signup__step--current"
              : i < currentIndex
                ? "signup__step signup__step--done"
                : "signup__step"
          }
        >
          {s.label}
        </li>
      ))}
    </ol>
  );
}

export default function Signup() {
  if (!isSupabaseEnabled) {
    return (
      <main className="page page--sign-in">
        <div className="sign-in">
          <AuthScreenHeader
            title="Set up your parish"
            subtitle="Signup isn't available in this environment."
          />
        </div>
      </main>
    );
  }
  return <SignupWizard />;
}

function SignupWizard() {
  const navigate = useNavigate();
  const [wizard, setWizard] = useLocalStorage<WizardState>(WIZARD_KEY, EMPTY_WIZARD);
  const [phase, setPhase] = useState<Phase>("boot");
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);

  // Step 1 form fields — initialized from the persisted wizard state
  // so a refresh restores what was typed. Password intentionally not
  // persisted anywhere.
  const [parishName, setParishName] = useState(wizard.parishName);
  const [timezone, setTimezone] = useState(wizard.timezone ?? TZ.initial);
  const [coordinatorName, setCoordinatorName] = useState(wizard.coordinatorName);
  const [email, setEmail] = useState(wizard.email);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [accountError, setAccountError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);

  // The parish created (or found) by /api/onboarding/signup.
  const [parish, setParish] = useState<ParishSummary | null>(null);

  // ensureParish must not double-fire when getSession and
  // onAuthStateChange race on the post-confirmation redirect.
  const ensuringRef = useRef(false);
  const wizardRef = useRef(wizard);
  wizardRef.current = wizard;

  async function ensureParish() {
    if (ensuringRef.current) return;
    ensuringRef.current = true;
    setPhase("creating-parish");
    try {
      const env = await apiPostEnvelope<{ parish: ParishSummary }>("/onboarding/signup", {
        parishName: wizardRef.current.parishName,
        coordinatorName: wizardRef.current.coordinatorName,
        timezone: wizardRef.current.timezone,
      });
      if (env.data) {
        const p = env.data.parish;
        setParish(p);
        if (p.status !== "pending") {
          // Provisioned already (trialing or beyond) — nothing left
          // for the wizard to do that /settings can't do better.
          setPhase("already-onboarded");
        } else if (!p.msp_connected && !wizardRef.current.connectSkipped) {
          setPhase("connect");
        } else {
          setPhase("brand");
        }
      } else if (env.error?.kind === "volunteer_account_exists") {
        setBlockedMessage(describeMspError(env.error));
        setPhase("blocked");
      } else if (env.error?.kind === "invalid_parish_name") {
        // A session with no parish and no locally saved name — e.g.
        // the confirmation email was opened on a different device.
        // Ask for the two Step 1 fields that aren't in the auth user.
        setPhase("authed-account");
      } else {
        setBlockedMessage(
          env.error?.message ?? "Could not create your parish. Please try again.",
        );
        setPhase("create-failed");
      }
    } catch {
      setBlockedMessage("Network problem — please check your connection and try again.");
      setPhase("create-failed");
    } finally {
      ensuringRef.current = false;
    }
  }

  useEffect(() => {
    if (!supabase) return;
    let cancelled = false;

    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      if (!data.session) {
        setPhase("account");
        return;
      }
      void ensureParish();
    });

    // Fires when the confirmation-email redirect establishes the
    // session after initial mount (detectSessionInUrl processing can
    // land after getSession resolves).
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) void ensureParish();
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
    // Mount-only: session changes are handled by the subscription.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submitAccount(e: FormEvent) {
    e.preventDefault();
    if (!supabase) return;
    if (password.length < MIN_PASSWORD_LENGTH) {
      setAccountError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    const trimmedParish = parishName.trim();
    if (!trimmedParish) {
      setAccountError("Enter your parish's name.");
      return;
    }

    setSubmitting(true);
    setAccountError(null);
    // Persist before the network call so the confirmation-email round
    // trip (a full page reload) still knows the parish name.
    setWizard({
      parishName: trimmedParish,
      coordinatorName: coordinatorName.trim(),
      email: email.trim(),
      timezone,
    });

    const { data, error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        // role "coordinator" so the handle_new_volunteer() trigger
        // (migration 0006, volunteers only) leaves this signup alone.
        // The users row is created by /api/onboarding/signup, not a
        // trigger — parish and account are born in the same flow.
        data: {
          role: "coordinator",
          full_name: coordinatorName.trim(),
          parish_name: trimmedParish,
        },
        emailRedirectTo: `${window.location.origin}/signup`,
      },
    });
    setSubmitting(false);

    // Live-probed Supabase semantics (lib/otp.ts): a re-signup on an
    // unconfirmed email succeeds and auto-resends the code; inside
    // the 60s send window it rate-limits instead; a finished account
    // comes back obfuscated (identities: []).
    const outcome = classifySignupResult({
      errorMessage: error?.message ?? null,
      identitiesLength: data?.user ? (data.user.identities ?? []).length : null,
      hasSession: Boolean(data?.session),
    });
    switch (outcome.kind) {
      case "signed-in":
        void ensureParish();
        return;
      case "code-sent":
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        setPhase("confirm-code");
        return;
      case "code-recently-sent":
        setResendCooldown(outcome.retryAfter);
        setPhase("confirm-code");
        return;
      case "already-confirmed":
        setAccountError(
          "You already have a finished account with this email. Sign in instead and your parish setup will resume where it left off.",
        );
        return;
      case "error":
        setAccountError(describeSignupError(outcome.message));
        return;
    }
  }

  // The authed variant of Step 1: session exists, parish doesn't, and
  // localStorage has nothing (different device). No email/password —
  // just the parish + name fields the worker needs.
  function submitAuthedAccount(e: FormEvent) {
    e.preventDefault();
    const trimmedParish = parishName.trim();
    if (!trimmedParish) {
      setAccountError("Enter your parish's name.");
      return;
    }
    setAccountError(null);
    setWizard({
      parishName: trimmedParish,
      coordinatorName: coordinatorName.trim(),
      email: wizardRef.current.email,
      timezone,
    });
    // setWizard is async state — ensureParish reads wizardRef, which
    // tracks renders, so pass through a microtask via the ref update
    // below instead of racing it.
    wizardRef.current = {
      ...wizardRef.current,
      parishName: trimmedParish,
      coordinatorName: coordinatorName.trim(),
      timezone,
    };
    void ensureParish();
  }

  function finish() {
    setWizard(EMPTY_WIZARD);
    window.localStorage.removeItem(WIZARD_KEY);
    navigate("/");
  }

  async function signOutAndRestart() {
    if (supabase) await supabase.auth.signOut();
    setParish(null);
    setPhase("account");
  }

  if (phase === "boot" || phase === "creating-parish") {
    return (
      <main className="page page--sign-in">
        <div className="sign-in">
          <AuthScreenHeader
            title="Set up your parish"
            subtitle={phase === "creating-parish" ? "Setting up your parish…" : "Loading…"}
          />
        </div>
      </main>
    );
  }

  if (phase === "already-onboarded") {
    return (
      <main className="page page--sign-in">
        <div className="sign-in">
          <AuthScreenHeader
            title="You're all set"
            subtitle="This parish is already set up. Branding and MSP settings live in Settings."
          />
          <button type="button" className="btn" onClick={() => navigate("/")}>
            Go to your dashboard
          </button>
          <div className="sign-in__alt">
            <button type="button" className="sign-in__link" onClick={signOutAndRestart}>
              Sign out and create a different parish
            </button>
          </div>
        </div>
        <PoweredByFooter />
      </main>
    );
  }

  if (phase === "blocked") {
    return (
      <main className="page page--sign-in">
        <div className="sign-in">
          <AuthScreenHeader title="Set up your parish" />
          <p className="sign-in__error">{blockedMessage}</p>
          <div className="sign-in__alt">
            <button type="button" className="sign-in__link" onClick={signOutAndRestart}>
              Start over with a different email
            </button>
          </div>
        </div>
        <PoweredByFooter />
      </main>
    );
  }

  if (phase === "create-failed") {
    return (
      <main className="page page--sign-in">
        <div className="sign-in">
          <AuthScreenHeader title="Set up your parish" />
          <p className="sign-in__error">{blockedMessage}</p>
          <button type="button" className="btn" onClick={() => void ensureParish()}>
            Try again
          </button>
        </div>
        <PoweredByFooter />
      </main>
    );
  }

  if (phase === "confirm-code") {
    return (
      <ConfirmCodeStep
        email={wizard.email}
        initialCooldown={resendCooldown}
        // Session established — the same continuation the old email
        // link used: create/find the parish and move the wizard on.
        onVerified={() => void ensureParish()}
        onBack={() => setPhase("account")}
        footer={<PoweredByFooter />}
      />
    );
  }

  if (phase === "connect") {
    return (
      <ConnectStep
        parish={parish}
        onConnected={() => setPhase("brand")}
        onSkip={() => {
          setWizard({ ...wizardRef.current, connectSkipped: true });
          setPhase("brand");
        }}
      />
    );
  }

  if (phase === "brand") {
    return <BrandStep parish={parish} onDone={() => setPhase("done")} />;
  }

  if (phase === "done") {
    return (
      <main className="page page--sign-in">
        <div className="sign-in">
          <AuthScreenHeader title="Welcome to Sacristy AI" />
          <div className="sign-in__confirmation">
            <p>
              <strong>{parish?.name ?? "Your parish"}</strong> is set up.
              {wizard.connectSkipped
                ? " Connect MSP from the banner on your dashboard whenever you have the API token."
                : " Your dashboard is reading live from MSP."}
            </p>
          </div>
          <button type="button" className="btn" onClick={finish}>
            Go to your dashboard
          </button>
        </div>
        <PoweredByFooter />
      </main>
    );
  }

  if (phase === "authed-account") {
    return (
      <main className="page page--sign-in">
        <div className="sign-in">
          <AuthScreenHeader
            title="Almost there"
            subtitle="Your account is confirmed — tell us about your parish to finish."
          />
        <StepIndicator current="account" />
          <form className="sign-in__form" onSubmit={submitAuthedAccount}>
            <label className="sign-in__field">
              <span className="sign-in__label">Parish name</span>
              <input
                type="text"
                className="sign-in__input"
                value={parishName}
                onChange={(e) => setParishName(e.target.value)}
                placeholder="St. Monica Catholic Church"
                autoComplete="organization"
                required
                autoFocus
              />
            </label>
            <label className="sign-in__field">
              <span className="sign-in__label">Timezone</span>
              <select
                className="sign-in__input"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
              >
                {TZ.options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <span className="sign-in__hint">
                So your Mass times always show in your parish's local time.
              </span>
            </label>
            <label className="sign-in__field">
              <span className="sign-in__label">Your name</span>
              <input
                type="text"
                className="sign-in__input"
                value={coordinatorName}
                onChange={(e) => setCoordinatorName(e.target.value)}
                autoComplete="name"
                required
              />
            </label>
            <button type="submit" className="btn">
              Continue
            </button>
            {accountError ? <p className="sign-in__error">{accountError}</p> : null}
          </form>
        </div>
        <PoweredByFooter />
      </main>
    );
  }

  return (
    <main className="page page--sign-in">
      <div className="sign-in">
        <AuthScreenHeader
          title="Set up your parish"
          subtitle="Early access. No card needed to start."
        />
        <StepIndicator current="account" />
        <form className="sign-in__form" onSubmit={submitAccount}>
          <label className="sign-in__field">
            <span className="sign-in__label">Parish name</span>
            <input
              type="text"
              className="sign-in__input"
              value={parishName}
              onChange={(e) => setParishName(e.target.value)}
              placeholder="St. Monica Catholic Church"
              autoComplete="organization"
              required
              autoFocus
            />
          </label>
          <label className="sign-in__field">
            <span className="sign-in__label">Timezone</span>
            <select
              className="sign-in__input"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
            >
              {TZ.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <span className="sign-in__hint">
              So your Mass times always show in your parish's local time.
            </span>
          </label>
          <label className="sign-in__field">
            <span className="sign-in__label">Your name</span>
            <input
              type="text"
              className="sign-in__input"
              value={coordinatorName}
              onChange={(e) => setCoordinatorName(e.target.value)}
              autoComplete="name"
              required
            />
          </label>
          <label className="sign-in__field">
            <span className="sign-in__label">Email</span>
            <input
              type="email"
              className="sign-in__input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@parish.org"
              autoComplete="email"
              required
            />
          </label>
          <label className="sign-in__field">
            <span className="sign-in__label">Password</span>
            <input
              type="password"
              className="sign-in__input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              minLength={MIN_PASSWORD_LENGTH}
              required
            />
          </label>
          <button type="submit" className="btn" disabled={submitting}>
            {submitting ? "Creating account…" : "Create account"}
          </button>
          {accountError ? <p className="sign-in__error">{accountError}</p> : null}
        </form>
        <div className="sign-in__alt">
          <span className="sign-in__lede">Already set up? </span>
          <a className="sign-in__link" href="/sign-in">
            Sign in
          </a>
        </div>
      </div>
      <PoweredByFooter />
    </main>
  );
}

type ConnectStatus = "idle" | "validating" | "connected";

function ConnectStep({
  parish,
  onConnected,
  onSkip,
}: {
  parish: ParishSummary | null;
  onConnected: () => void;
  onSkip: () => void;
}) {
  const [token, setToken] = useState("");
  const [status, setStatus] = useState<ConnectStatus>(
    parish?.msp_connected ? "connected" : "idle",
  );
  const [result, setResult] = useState<MspValidateResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submitToken(e: FormEvent) {
    e.preventDefault();
    if (!token.trim()) return;
    setStatus("validating");
    setError(null);
    try {
      const env = await apiPostEnvelope<MspValidateResult>("/onboarding/msp-validate", {
        token: token.trim(),
      });
      if (env.data) {
        setResult(env.data);
        setStatus("connected");
      } else {
        setError(describeMspError(env.error));
        setStatus("idle");
      }
    } catch {
      setError("Network problem — your token wasn't checked. Please try again.");
      setStatus("idle");
    }
  }

  if (status === "connected") {
    return (
      <main className="page page--sign-in">
        <div className="sign-in">
          <AuthScreenHeader title="Connected" />
          <StepIndicator current="connect" />
          <div className="sign-in__confirmation">
            {result ? (
              <>
                <p>
                  Connected to <strong>{result.parishName}</strong> —{" "}
                  {result.volunteerCount} volunteers, {result.ministryCount}{" "}
                  {result.ministryCount === 1 ? "ministry" : "ministries"}.
                </p>
                {result.ministryNames.length > 0 ? (
                  <p className="signup__ministries">{result.ministryNames.join(" · ")}</p>
                ) : null}
              </>
            ) : (
              <p>Your MSP account is already connected.</p>
            )}
          </div>
          <button type="button" className="btn" onClick={onConnected}>
            Continue
          </button>
        </div>
        <PoweredByFooter />
      </main>
    );
  }

  return (
    <main className="page page--sign-in">
      <div className="sign-in">
        <AuthScreenHeader
          title="Connect Ministry Scheduler Pro"
          subtitle={`Sacristy AI reads your schedule from MSP${
            parish ? ` for ${parish.name}` : ""
          }. It never changes anything — read-only, always.`}
        />
        <StepIndicator current="connect" />
        <form className="sign-in__form" onSubmit={submitToken}>
          <label className="sign-in__field">
            <span className="sign-in__label">MSP API token</span>
            <input
              type="password"
              className="sign-in__input"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              required
            />
          </label>
          <details className="signup__help">
            <summary>Where do I find this?</summary>
            <p>
              API tokens are issued by Rotunda Software, the makers of MSP. If your parish is
              on <strong>Plus Edition</strong>, email{" "}
              <a href="mailto:support@rotundasoftware.com">support@rotundasoftware.com</a> and
              ask for a REST API key for your organization — your parish office may already
              have one on file. API access requires Plus Edition.
            </p>
            <p>
              Sacristy AI uses the token to <em>read</em> your volunteers, ministries, and
              schedules. Your data stays in MSP.{" "}
              <a href="/help/connect-msp" target="_blank" rel="noreferrer">
                Read the full guide
              </a>
              .
            </p>
          </details>
          <button type="submit" className="btn" disabled={status === "validating"}>
            {status === "validating" ? "Checking with MSP…" : "Connect"}
          </button>
          {error ? <p className="sign-in__error">{error}</p> : null}
        </form>
        <div className="sign-in__alt">
          <button type="button" className="sign-in__link" onClick={onSkip}>
            Skip for now
          </button>
          <p className="sign-in__lede signup__skip-note">
            You can connect MSP later — we'll remind you on your dashboard.
          </p>
        </div>
      </div>
      <PoweredByFooter />
    </main>
  );
}

// Step 3 — optional branding. Reuses the admin settings + logo
// endpoints (the coordinator's users row exists by now, so the admin
// role gate passes). Defaults: parish name as display name, standard
// accent, no logo — "Skip" applies exactly those by doing nothing.
function BrandStep({
  parish,
  onDone,
}: {
  parish: ParishSummary | null;
  onDone: () => void;
}) {
  const [displayName, setDisplayName] = useState(parish?.name ?? "");
  const [accent, setAccent] = useState("#3B6FB0");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(e: FormEvent) {
    e.preventDefault();
    const trimmed = displayName.trim();
    if (!trimmed) {
      setError("Enter a display name, or skip to use your parish name.");
      return;
    }
    if (!HEX_RE.test(accent)) {
      setError("Accent color must be a 6-digit hex value like #3B6FB0.");
      return;
    }
    if (logoFile && logoFile.size > MAX_LOGO_BYTES) {
      setError("Logo must be under 500 KB.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await apiPatch("/parish/settings", { display_name: trimmed, accent_color: accent });
      if (logoFile) {
        const form = new FormData();
        form.append("logo", logoFile);
        await apiPostFormData("/parish/logo", form);
      }
      onDone();
    } catch {
      setError("Couldn't save branding. You can also set this later in Settings.");
      setSaving(false);
    }
  }

  return (
    <main className="page page--sign-in">
      <div className="sign-in">
        <AuthScreenHeader
          title="Make it yours"
          subtitle="Thirty seconds of polish for your volunteers' pages — or skip and use the defaults."
        />
        <StepIndicator current="brand" />
        <form className="sign-in__form" onSubmit={save}>
          <label className="sign-in__field">
            <span className="sign-in__label">Display name</span>
            <input
              type="text"
              className="sign-in__input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
            />
          </label>
          <label className="sign-in__field">
            <span className="sign-in__label">Accent color</span>
            <span className="signup__accent-row">
              <input
                type="color"
                className="signup__color"
                value={HEX_RE.test(accent) ? accent : "#3B6FB0"}
                onChange={(e) => setAccent(e.target.value)}
                aria-label="Pick accent color"
              />
              <input
                type="text"
                className="sign-in__input signup__hex"
                value={accent}
                onChange={(e) => setAccent(e.target.value)}
                spellCheck={false}
              />
            </span>
          </label>
          <label className="sign-in__field">
            <span className="sign-in__label">Parish logo (optional)</span>
            <input
              type="file"
              className="signup__file"
              accept={ALLOWED_LOGO_MIMES}
              onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)}
            />
            <span className="sign-in__lede signup__file-hint">
              PNG, SVG, or JPEG up to 500 KB.
            </span>
          </label>
          <button type="submit" className="btn" disabled={saving}>
            {saving ? "Saving…" : "Save and continue"}
          </button>
          {error ? <p className="sign-in__error">{error}</p> : null}
        </form>
        <div className="sign-in__alt">
          <button type="button" className="sign-in__link" onClick={onDone}>
            Skip — use defaults
          </button>
        </div>
      </div>
      <PoweredByFooter />
    </main>
  );
}
